// Sugalarm — raw WebSocket wrapper.
//
// Owns:
//   • opening and closing the socket
//   • parsing JSON frames and handing them to the consumer in BATCHES
//   • reconnection with exponential backoff (1s → 2s → 4s → … → 30s cap),
//     reset on a successful frame
//   • a 30-second ping whose pong is now WATCHED (cycle 429)
//   • a frame-age watchdog (cycle 429)
//   • `reopen()` — an unconditional close-and-reconnect (cycle 429)
//
// `useGlucoseSocket` builds on top of this; this module has no React.
//
// ⚠ CYCLE 429 — WHY THIS FILE CHANGED. On 2026-09-10 every household
// iPhone came to the foreground with a socket iOS had killed at the NAT
// while the app was frozen, and the socket still reported `readyState
// OPEN`. Nothing here noticed: the ping was sent into the void, the pong
// never came back and nobody was listening for it, no frame arrived and
// nothing measured how long ago the last one had. The wake handler saw
// OPEN and left it alone. The reading froze while the native widget
// stayed correct, the client's alert slot stayed bound to a stale row, and
// every "I'm treating this" tap went to that row and was refused. When a
// socket instead SURVIVED the freeze, the whole backlog arrived in one
// burst on foreground and every buffered event fired its sound cue.
//
// ⚠ CYCLE 433 — WHY THIS FILE CHANGED AGAIN. On 2026-09-13 at 23:14:50Z,
// during a real urgent low, a phone sat in the foreground for 88 seconds
// with no socket and no wake handling, showing 39 while the member's own
// receiver read 52; the wake reached the server 8.5 minutes later with no
// reopen, no reseed and no socket behind it. Cycle 429's watchdog could not
// have caught it: it was started in `onopen` and cleared in `onclose`, so a
// socket stuck in `CONNECTING`, the reconnect backoff and everything behind
// the consumer's wake guard were unwatched — and what it DID judge was
// frame age, which counts pongs. A link answering pings while delivering no
// glucose looked perfectly healthy.
//
// Two rules added:
//   4. EVERY STATE HAS A BOUND. The watchdog runs from `start()` to
//      `stop()`, not from `onopen` to `onclose`, and judges whichever
//      `TransportPhase` the socket is in: `open` (frame age, pong,
//      reading age), `connecting` (`CONNECT_TIMEOUT_MS`), `waiting`
//      (`RETRY_OVERDUE_MS`). While the page is visible the backoff itself
//      may not exceed `VISIBLE_BACKOFF_MAX_MS`.
//   5. A PONG IS NOT A READING. `readingConfirmedAt` is stamped only by a
//      `snapshot` or `glucose` frame (or the consumer's REST reseed, via
//      `noteReadingConfirmed`); past `READING_CONFIRM_BOUND_MS` the socket
//      is reopened AND the consumer is asked to reseed.
//
// Three rules from cycle 429, all still here:
//   1. NEVER TRUST `OPEN` AFTER A FREEZE. `reopen()` closes whatever is
//      there and connects fresh; the consumer calls it on every wake.
//   2. SILENCE IS DEATH. While the page is visible, no frame of any kind
//      within `FRAME_SILENCE_MS`, or no pong within `PONG_TIMEOUT_MS` of a
//      ping, means the socket is dead: reopen it.
//   3. A BACKLOG IS STATE, NOT EVENTS. Frames are delivered in batches; a
//      batch that is plainly a backlog (a burst, or anything that lands
//      right after the timers thaw, or the first batch after a connect) is
//      marked `catchUp` so the consumer applies it as state and mints no
//      event from it. One cue for the state that results, never one per
//      frame.
//
// Every dependency on the browser (the socket constructor, timers, the
// clock, visibility) is injectable so the rules are testable under node
// with a fake socket; production uses the globals.

import { wsUrl } from "../env";
import { recordClientEvent } from "./clientEvents";
import {
  READING_CONFIRM_BOUND_MS,
  readingConfirmationOverdue,
} from "./readingFreshness";
import {
  READING_BOUND_CEILING_MS,
  READING_BOUND_FLOOR_MS,
} from "./readingCadence";
import type { WSFrame } from "./types";

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

/** What the consumer learns about a delivered batch. */
export interface DeliveryMeta {
  /** Epoch ms the batch was handed over. */
  receivedAtMs: number;
  /** The batch is a BACKLOG or a (re)connect seed: apply as state, mint no
   *  events, play at most one cue for the state that results. */
  catchUp: boolean;
  /** How many frames the batch carried. */
  size: number;
}

export interface Handlers {
  onFrames: (frames: WSFrame[], meta: DeliveryMeta) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Cycle 433 — the transport asking its consumer to RESEED over REST.
   *  Fired when the reading-age bound trips: a reopen alone proves nothing
   *  if the fresh socket is as quiet as the old one, so the reading-age
   *  case reconnects AND asks. Optional; a consumer that omits it simply
   *  gets the reconnect. */
  onReseedNeeded?: (reason: ReopenReason) => void;
}

/** The subset of `WebSocket` this module touches, so a test can hand in a
 *  fake and drive it by hand. */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
}

export interface SocketDeps {
  makeSocket: (url: string) => SocketLike;
  urlFor: (token: string) => string;
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => number;
  clearTimeout: (id: number) => void;
  setInterval: (fn: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
  /** Is the page visible right now? The watchdog only judges a visible
   *  page — a hidden one is expected to be quiet (and frozen). */
  isVisible: () => boolean;
}

export const WS_READY_CONNECTING = 0;
export const WS_READY_OPEN = 1;
export const WS_READY_CLOSED = 3;

export const PING_INTERVAL_MS = 30_000;
/** A pong not back within this of its ping means the socket is dead. */
export const PONG_TIMEOUT_MS = 10_000;
/** No frame of ANY kind (pongs included) within this, on a visible page,
 *  means the socket is dead: three ping intervals or a minute, whichever
 *  is shorter. */
export const FRAME_SILENCE_MS = Math.min(3 * PING_INTERVAL_MS, 60_000);
/** How often the watchdog looks. */
export const WATCHDOG_TICK_MS = 5_000;
/** Frames that arrive within this of each other are one batch. Adds at
 *  most this much latency to a lone live frame. */
export const BATCH_WINDOW_MS = 50;
/** A batch at least this large is a backlog: frames normally arrive
 *  seconds apart, so three inside one window only happens on a thaw. */
export const BURST_FRAMES = 3;
/** A watchdog tick this late means the timers were frozen (the webview
 *  was suspended); a backlog may be about to land. */
export const THAW_GAP_MS = 2 * WATCHDOG_TICK_MS;
/** How long after a thaw every batch counts as catch-up. */
export const THAW_CATCHUP_WINDOW_MS = 1_500;

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

// ─── Cycle 433 — the watchdog is bounded in EVERY state ──────────────────
//
// Until this cycle the watchdog was started in `onopen` and cleared in
// `onclose`, so it judged exactly one state: a socket that had opened. It
// was UNBOUNDED in three others, and the third is the one that held the
// screen for 88 seconds on 2026-09-13:
//   • a socket stuck in `CONNECTING` that never reaches `onopen` — no
//     watchdog was ever started for it;
//   • the reconnect backoff, up to 30 s, with the watchdog cleared;
//   • behind the consumer's wake guard, where nothing was running at all.
// The watchdog now runs for the whole life of the socket object — from
// `start()` to `stop()` — and every state it can be in has a bound.

/** A socket that has not reached `onopen` within this, on a visible page,
 *  is abandoned and a fresh attempt is made. Ten seconds: a WebSocket
 *  handshake that has not completed in ten seconds on a foreground alarm
 *  device is not going to. */
export const CONNECT_TIMEOUT_MS = 10_000;

/** ⚠ WHILE THE PAGE IS VISIBLE THE RECONNECT BACKOFF MAY NOT EXCEED THIS.
 *  The 1 s → 30 s ladder is right for a backgrounded tab and wrong for the
 *  screen someone is looking at during a low: thirty seconds of nothing,
 *  with the last number still painted, is the failure this cycle exists to
 *  end. Hidden pages keep the full `BACKOFF_MAX_MS` ladder. */
export const VISIBLE_BACKOFF_MAX_MS = 10_000;

/** A scheduled retry that has produced no new socket by this is forced by
 *  the watchdog — the backstop for a `setTimeout` the webview froze or
 *  dropped. One tick past the visible cap, so it can never race a retry
 *  that is about to fire on its own. */
export const RETRY_OVERDUE_MS = VISIBLE_BACKOFF_MAX_MS + WATCHDOG_TICK_MS;

/** Which of the transport's states the socket is in right now. The
 *  watchdog judges each one by its own bound. */
export type TransportPhase =
  /** A socket exists and has fired `onopen`. */
  | "open"
  /** A socket exists and has not fired `onopen`. */
  | "connecting"
  /** No socket; a reconnect is scheduled. */
  | "waiting"
  /** Not running (before `start()` / after `stop()`). Never judged. */
  | "none";

export type ReopenReason =
  | "wake"
  | "silence"
  | "pong"
  | "manual"
  /** Cycle 433 — pongs kept coming and readings did not. */
  | "reading_age"
  /** Cycle 433 — `CONNECTING` never became `onopen`. */
  | "connect_timeout"
  /** Cycle 433 — the scheduled retry never produced a socket. */
  | "backoff";

export interface TransportStats {
  /** Reopens forced by the watchdog (any of its bounds). */
  watchdogReconnects: number;
  /** Reopens forced by a wake signal. */
  wakeReopens: number;
  reopensByReason: Record<ReopenReason, number>;
  /** Cycle 433 — bounded reseeds that hit `RESEED_TIMEOUT_MS` and were
   *  aborted. Non-zero means a fetch was frozen mid-flight: the 2026-09-13
   *  mechanism, now visible instead of silent. */
  reseedTimeouts: number;
  /** Cycle 433 — reopens forced because the reading stream went quiet
   *  while the socket looked healthy. */
  readingAgeReopens: number;
  /** Cycle 433 — token refreshes that joined an in-flight one instead of
   *  starting a second. The cycle-419 storm, counted. */
  refreshCoalesced: number;
  /** Cycle 433 — token refreshes that hit `REFRESH_TIMEOUT_MS` and were
   *  aborted. The single-flight refresh's frozen-fetch signal: without the
   *  bound this is the case that wedged the whole page. */
  refreshTimeouts: number;
  /** ⚠ CYCLE 434 — refreshes this PAGE LOAD actually initiated (as opposed
   *  to joined). The one number that separates the two live explanations
   *  for the 07:49 burst: fourteen starts from ONE load means the shared
   *  slot failed inside a single context; one start per load means the
   *  fourteen came from fourteen contexts. Nothing else on the server can
   *  tell those apart — see `PAGE_LOAD_ID`. */
  refreshStarts: number;
  /** ⚠ CYCLE 434 — the reading-age bound this load is CURRENTLY judging
   *  itself by, in ms, derived from the active member's own feed. A gauge,
   *  not a counter: it is the last value set, and it is reported so the
   *  next diagnostic can see what each client was using rather than
   *  assuming everyone was on the same constant. That assumption is what
   *  made the 6.8-minute feed's 25 reopens look like a fault. */
  readingBoundMs: number;
}

const emptyReasons = (): Record<ReopenReason, number> => ({
  wake: 0,
  silence: 0,
  pong: 0,
  manual: 0,
  reading_age: 0,
  connect_timeout: 0,
  backoff: 0,
});

const stats: TransportStats = {
  watchdogReconnects: 0,
  wakeReopens: 0,
  reopensByReason: emptyReasons(),
  reseedTimeouts: 0,
  readingAgeReopens: 0,
  refreshCoalesced: 0,
  refreshTimeouts: 0,
  refreshStarts: 0,
  readingBoundMs: READING_CONFIRM_BOUND_MS,
};

/** Process-wide transport counters (this page load). Posted on the next
 *  activation report as the install's `note` so the fleet can be seen. */
export function transportStats(): TransportStats {
  return {
    watchdogReconnects: stats.watchdogReconnects,
    wakeReopens: stats.wakeReopens,
    reopensByReason: { ...stats.reopensByReason },
    reseedTimeouts: stats.reseedTimeouts,
    readingAgeReopens: stats.readingAgeReopens,
    refreshCoalesced: stats.refreshCoalesced,
    refreshTimeouts: stats.refreshTimeouts,
    refreshStarts: stats.refreshStarts,
    readingBoundMs: stats.readingBoundMs,
  };
}

/** Test seam: set the bound the note reports, without a socket. The
 *  clamping that matters lives on `GlucoseSocket.setReadingBound`; this
 *  exists so the note's 200-character budget can be driven directly. */
export function setReadingBoundForNote(ms: number): void {
  stats.readingBoundMs = ms;
}

/** The activation-report line. Counts only, never a value.
 *
 *  ⚠ Cycle 433's three counters are appended ONLY when non-zero. The
 *  server's `note` column is 200 characters and the cycle-429 shape is
 *  pinned byte-for-byte by its own test; a healthy install keeps sending
 *  exactly the line it sent before, and an install that hit one of the new
 *  states says so. `reseed_timeouts` and `refresh_coalesced` ride here
 *  rather than on the event rail because the server's client-claimed kind
 *  list is closed and this cycle changes nothing on the server — see the
 *  CHANGELOG's note on the two kinds that still need a server-side line. */
export function transportNote(): string {
  // ⚠ CYCLE 434 — THE NOTE IS ASSEMBLED BY PRIORITY AND BOUNDED, BECAUSE
  // IT IS TRUNCATED AT 200 CHARACTERS AND THE MOST VALUABLE FIELD WAS LAST.
  // `collectActivationReport` does `transportNote().slice(0, 200)` and the
  // server's column caps at 200 too. The cycle-434 draft appended the
  // carry-over block at the END, so on a loaded note — the only kind a
  // stormy install produces — the `prev_*` fields were the FIRST thing cut,
  // and those are exactly what deliverable 4 exists to capture. Segments
  // are now emitted in decreasing order of what they settle, and a segment
  // is taken WHOLE or not at all, so the note can never end in half a
  // field that a grep would then misread.
  const base = `ws_watchdog_reconnects=${stats.watchdogReconnects} ws_wake_reopens=${stats.wakeReopens}`;
  const segments: string[] = [];

  // 1. THIS load's refresh shape — one context storming, or not.
  if (stats.refreshStarts > 0 || stats.refreshCoalesced > 0) {
    segments.push(
      `refresh_starts=${stats.refreshStarts}` +
        ` refresh_coalesced=${stats.refreshCoalesced}` +
        ` load=${PAGE_LOAD_ID}`,
    );
  }
  // 2. The PREVIOUS load's, for a page that stormed and then reloaded —
  //    the case the first activation report structurally cannot carry.
  //    `prev_coalesced` rides only when non-zero: a zero is the expected
  //    reading and buys room for a field that is not.
  if (previousLoad != null) {
    segments.push(
      `prev_starts=${previousLoad.starts}` +
        (previousLoad.coalesced > 0
          ? ` prev_coalesced=${previousLoad.coalesced}`
          : "") +
        ` prev_load=${previousLoad.load}`,
    );
  }
  // 3. Which bound this client judged itself by — only once DERIVED, so a
  //    client on the fallback still sends the cycle-429 line byte for byte.
  if (stats.readingBoundMs !== READING_CONFIRM_BOUND_MS) {
    segments.push(`reading_bound_s=${Math.round(stats.readingBoundMs / 1000)}`);
  }
  // 4. The reading-age churn this cycle set out to stop.
  if (stats.readingAgeReopens > 0) {
    segments.push(`reading_age_reopens=${stats.readingAgeReopens}`);
  }
  // 5. A frozen refresh — the wedge hazard.
  if (stats.refreshTimeouts > 0) {
    segments.push(`refresh_timeouts=${stats.refreshTimeouts}`);
  }
  // 6. A frozen reseed — the 2026-09-13 mechanism.
  if (stats.reseedTimeouts > 0) {
    segments.push(`reseed_timeouts=${stats.reseedTimeouts}`);
  }

  let note = base;
  for (const seg of segments) {
    if (note.length + 1 + seg.length > NOTE_MAX_CHARS) continue;
    note += ` ${seg}`;
  }
  return note;
}

/** Test seam: zero the counters. */
export function resetTransportStats(): void {
  stats.watchdogReconnects = 0;
  stats.wakeReopens = 0;
  stats.reopensByReason = emptyReasons();
  stats.reseedTimeouts = 0;
  stats.readingAgeReopens = 0;
  stats.readingBoundMs = READING_CONFIRM_BOUND_MS;
  stats.refreshCoalesced = 0;
  stats.refreshTimeouts = 0;
  stats.refreshStarts = 0;
}

/** Cycle 433 — a bounded reseed hit its timeout and was aborted. Counted
 *  here so it reaches the activation report's note. */
export function noteReseedTimeout(): void {
  stats.reseedTimeouts += 1;
}

/** Cycle 433 — a token refresh joined the one already in flight. */
export function noteRefreshCoalesced(): void {
  stats.refreshCoalesced += 1;
  persistRefreshDiag();
}

/** Cycle 433 — the shared token refresh hit its bound and was aborted. */
export function noteRefreshTimeout(): void {
  stats.refreshTimeouts += 1;
}

/** Cycle 434 — this page load STARTED a refresh (did not join one). */
export function noteRefreshStart(): void {
  stats.refreshStarts += 1;
  persistRefreshDiag();
}

/** ⚠ CYCLE 434 — THE PAGE LOAD'S OWN ID, AND WHY IT EXISTS.
 *
 *  The 07:49:15Z burst was fourteen refreshes with zero joins from one
 *  address, and the server cannot say whether that was one JS context whose
 *  shared slot failed or fourteen contexts each doing one. It cannot say it
 *  because `log_lines.client_id` is REGEX-PARSED OUT OF THE RENDERED
 *  MESSAGE (`log_sink.parse_ids`), and a `uvicorn.access` line renders as
 *  `addr - "METHOD /path HTTP/1.1" STATUS` with no `client=` in it. Verified
 *  against production: a request carrying `X-Sugalarm-Client-Id` produced a
 *  row with `client_id` NULL and the id present in no message anywhere. So
 *  adding a header to the refresh POST would change nothing, and the only
 *  other place to put it is an application log line on the refresh path —
 *  which is a SERVER change this cycle does not have.
 *
 *  The activation report is the channel the SPA owns end to end: it carries
 *  a real `client_id` column and a free-text `note`. One id minted per page
 *  load, reported beside `refresh_starts`, is what separates the two
 *  explanations — two reports from the same install with different `load`
 *  values are two contexts. */
export function makeLoadId(): string {
  // ⚠ NOT `Math.random().toString(36).slice(2, 10)`. That can return a
  // ONE-character id (0.5 renders as "0.i"), and this value is read
  // precisely to tell two JS contexts apart — a collision defeats the
  // whole instrument. No unpredictability is needed (it authenticates
  // nothing and is never a lookup key); uniform length is.
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function") {
      return c.randomUUID().replace(/-/g, "").slice(0, 8);
    }
  } catch {
    /* fall through */
  }
  return Math.random().toString(36).slice(2).padEnd(8, "0").slice(0, 8);
}

export const PAGE_LOAD_ID: string = makeLoadId();

// ⚠ CYCLE 434 — THE COUNTERS SURVIVE A RELOAD, AND THEY HAVE TO.
//
// The first activation report of a page load CANNOT carry that load's own
// cold-start burst: `useActivationGuard.run()` snapshots `transportNote()`
// via `collectActivationReport` and only THEN posts it, and the post is
// itself one of the requests that 401s in the burst. So the first report
// always reads `refresh_starts=0`, and the burst is reported only by a
// LATER report on the same load — visibilitychange, window focus, or the
// 15-minute interval. A page that loads, storms and reloads before any of
// those would lose the numbers entirely, which is the one case the
// diagnostic most needs to catch.
//
// So each refresh start/join is mirrored to `localStorage` under this load's
// id, and the NEXT load reads any record left by a PREVIOUS one and reports
// it once. Best-effort throughout: storage that throws simply loses the
// diagnostic, never the transport.
const DIAG_KEY = "sugalarm.refreshDiag";

/** The activation note's hard budget. `collectActivationReport` slices at
 *  200 and the server's `note` column is `max_length=200`; assembling
 *  against the same number here means the slice is a no-op and no field is
 *  ever cut in half. */
const NOTE_MAX_CHARS = 200;

interface RefreshDiag {
  load: string;
  starts: number;
  coalesced: number;
}

/** A previous page load's refresh numbers, read once at module init and
 *  reported once. `null` when there were none or storage refused. */
let previousLoad: RefreshDiag | null = null;

try {
  const raw = globalThis.localStorage?.getItem(DIAG_KEY);
  if (raw) {
    // ⚠ CLEARED BEFORE THE PARSE, NOT AFTER. A record whose JSON is
    // corrupt used to skip its own `removeItem` and be re-parsed on every
    // load forever — inert, but it never drains.
    globalThis.localStorage?.removeItem(DIAG_KEY);
    const parsed = JSON.parse(raw) as RefreshDiag;
    if (
      parsed &&
      typeof parsed.load === "string" &&
      // Defence against an id collision only: this load cannot have
      // written a record before module init reads one, so nothing in the
      // ordinary lifecycle makes this clause fire. It is deliberately
      // unpinned — see the note in `refreshDiagCarryOver.test.ts`.
      parsed.load !== PAGE_LOAD_ID &&
      // ⚠ SHAPE-CHECKED, because these values are interpolated straight
      // into the activation note and anything that can write same-origin
      // storage can put them there. It discloses nothing new — `note` is
      // already a client-supplied field — but the admin roster should be
      // able to trust the field's shape.
      /^[a-z0-9]{1,12}$/.test(parsed.load) &&
      typeof parsed.starts === "number" &&
      Number.isInteger(parsed.starts) &&
      parsed.starts > 0 &&
      typeof parsed.coalesced === "number" &&
      Number.isInteger(parsed.coalesced) &&
      parsed.coalesced >= 0
    ) {
      previousLoad = parsed;
    }
  }
} catch {
  /* a diagnostic must never break the transport that makes it */
}

function persistRefreshDiag(): void {
  try {
    globalThis.localStorage?.setItem(
      DIAG_KEY,
      JSON.stringify({
        load: PAGE_LOAD_ID,
        starts: stats.refreshStarts,
        coalesced: stats.refreshCoalesced,
      }),
    );
  } catch {
    /* ignore */
  }
}

/** Test seam: forget the carried-over record. */
export function resetRefreshDiag(): void {
  previousLoad = null;
}

/** Every watchdog reason. A reopen under any of these is a `watchdog_reopen`
 *  on the client-claimed rail, with the reason in its details — the kind
 *  the server already knows, carrying the three states cycle 433 added. */
const WATCHDOG_REASONS: readonly ReopenReason[] = [
  "silence",
  "pong",
  "reading_age",
  "connect_timeout",
  "backoff",
];

/** ⚠ Cycle 435 — WHAT THE TRANSPORT KNEW AT THE MOMENT IT REOPENED. The
 *  server has accepted `ready_state` and `ms_since_last_frame` on
 *  `wake_reopen`, and `silence_ms` on `watchdog_reopen`, since cycle 431;
 *  the client only ever wrote `{ reason }` — 0 of 431 rows carried them,
 *  which is why 59 of 69 wake bursts could not be classified as
 *  socket-down or not. `held_reading_age_s` is the one new key (the one
 *  server line this cycle permits): the age of the reading the client was
 *  holding at the wake, which turns an unconfirmable burst into a measured
 *  one. Scalars only; the server's cleaner keeps ints as ints. */
export interface ReopenSnapshot {
  /** The WebSocket's `readyState` at the reopen, as a label; `none` when no
   *  socket object existed. */
  readyState: "connecting" | "open" | "closing" | "closed" | "none";
  /** ms since the last frame of ANY kind (pongs included); `null` if this
   *  socket never delivered one. */
  msSinceLastFrame: number | null;
  /** Age in whole seconds of the reading the consumer was holding, or
   *  `null` when it held none. */
  heldReadingAgeS: number | null;
}

export function readyStateLabel(rs: number | undefined | null): ReopenSnapshot["readyState"] {
  switch (rs) {
    case 0:
      return "connecting";
    case 1:
      return "open";
    case 2:
      return "closing";
    case 3:
      return "closed";
    default:
      return "none";
  }
}

/** Count a reopen and (cycle 431) record it as a client-claimed event for
 *  the next activation report — a wake reopen and a watchdog reopen each
 *  under their own kind, with the reason and (cycle 435) what the transport
 *  knew at that moment. Exported for its test. */
export function recordReopen(reason: ReopenReason, snap?: ReopenSnapshot): void {
  stats.reopensByReason[reason] += 1;
  if (reason === "wake") {
    stats.wakeReopens += 1;
    recordClientEvent("wake_reopen", {
      reason,
      ready_state: snap?.readyState,
      ms_since_last_frame: snap?.msSinceLastFrame ?? undefined,
      held_reading_age_s: snap?.heldReadingAgeS ?? undefined,
    });
  }
  if (reason === "reading_age") stats.readingAgeReopens += 1;
  if (WATCHDOG_REASONS.includes(reason)) {
    stats.watchdogReconnects += 1;
    // `watchdog_reopen` accepts `silence_ms` and nothing else new — the
    // server's key list for that kind is unchanged, so nothing else rides.
    recordClientEvent("watchdog_reopen", {
      reason,
      silence_ms: snap?.msSinceLastFrame ?? undefined,
    });
  }
}

/** Is a delivered batch a BACKLOG? Pure so the rule is pinned without a
 *  socket: a burst (`size >= BURST_FRAMES`), anything inside the thaw
 *  window, or the first delivery after a connect. */
export function classifyBatch(
  size: number,
  receivedAtMs: number,
  thawedUntilMs: number | null,
  firstAfterConnect: boolean,
): boolean {
  if (firstAfterConnect) return true;
  if (size >= BURST_FRAMES) return true;
  if (thawedUntilMs != null && receivedAtMs < thawedUntilMs) return true;
  return false;
}

/** Pure watchdog verdict for one tick of an OPEN socket, on frame age and
 *  the outstanding pong (cycle 429). `null` = healthy by those two rules.
 *  Cycle 433 left this function exactly as it was and added the reading-age
 *  rule beside it — the two answer different questions and a socket can
 *  fail either. */
export function watchdogVerdict(input: {
  nowMs: number;
  visible: boolean;
  lastFrameAtMs: number;
  pongDeadlineMs: number | null;
}): ReopenReason | null {
  if (!input.visible) return null;
  if (input.pongDeadlineMs != null && input.nowMs > input.pongDeadlineMs) {
    return "pong";
  }
  if (input.nowMs - input.lastFrameAtMs > FRAME_SILENCE_MS) return "silence";
  return null;
}

/** Cycle 433 — the pure verdict for READING age on an open socket. A link
 *  delivering heartbeats and no glucose looks perfectly healthy to
 *  `watchdogVerdict` while the number on screen goes stale, which is
 *  exactly what the 2026-09-13 phone displayed. `null` = the reading
 *  stream is being heard. */
export function readingAgeVerdict(input: {
  nowMs: number;
  visible: boolean;
  readingConfirmedAtMs: number | null;
  boundMs?: number;
}): ReopenReason | null {
  if (!input.visible) return null;
  return readingConfirmationOverdue(
    input.readingConfirmedAtMs,
    input.nowMs,
    input.boundMs ?? READING_CONFIRM_BOUND_MS,
  )
    ? "reading_age"
    : null;
}

/** Cycle 433 — the pure verdict for the two states that have no socket to
 *  judge: one stuck in `CONNECTING`, and a scheduled retry that has not
 *  produced a socket. `null` = still inside its bound. */
export function pendingVerdict(input: {
  nowMs: number;
  visible: boolean;
  phase: TransportPhase;
  phaseSinceMs: number;
}): ReopenReason | null {
  if (!input.visible) return null;
  const age = input.nowMs - input.phaseSinceMs;
  if (input.phase === "connecting") {
    return age > CONNECT_TIMEOUT_MS ? "connect_timeout" : null;
  }
  if (input.phase === "waiting") {
    return age > RETRY_OVERDUE_MS ? "backoff" : null;
  }
  return null;
}

function defaultDeps(): SocketDeps {
  return {
    makeSocket: (url) => new WebSocket(url) as unknown as SocketLike,
    urlFor: (token) => wsUrl("/ws/glucose", token),
    now: () => Date.now(),
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number,
    clearTimeout: (id) => globalThis.clearTimeout(id),
    setInterval: (fn, ms) => globalThis.setInterval(fn, ms) as unknown as number,
    clearInterval: (id) => globalThis.clearInterval(id),
    isVisible: () =>
      typeof document === "undefined" || document.visibilityState !== "hidden",
  };
}

export class GlucoseSocket {
  private ws: SocketLike | null = null;
  private gen = 0;
  private pingTimer: number | null = null;
  private watchdogTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private flushTimer: number | null = null;
  private pending: WSFrame[] = [];
  private backoff = BACKOFF_INITIAL_MS;
  private closed = false;
  private token: string;
  private handlers: Handlers;
  private deps: SocketDeps;
  private lastFrameAt = 0;
  private lastTickAt: number | null = null;
  private pongDeadline: number | null = null;
  private thawedUntil: number | null = null;
  private firstBatchAfterConnect = true;
  // Cycle 433 — which state the transport is in, and since when. The
  // watchdog bounds every one of them.
  private phase: TransportPhase = "none";
  private phaseSince = 0;
  // Cycle 433 — the last moment the SERVER said what the newest reading is
  // (a reading-bearing frame here, or a REST reseed via
  // `noteReadingConfirmed`). Not a pong: the 2026-09-13 socket answered
  // pings and delivered nothing. `null` = never, which is what the DISPLAY
  // is told; the watchdog measures from `readingAnchorAt` instead.
  private readingConfirmedAt: number | null = null;
  // ⚠ The watchdog's reference, which is NOT the same thing. A socket that
  // has just started has not yet had a chance to hear a reading, so the
  // bound runs from `start()`; and a reading-age reopen re-anchors, so a
  // link that stays quiet is reopened once per bound rather than once per
  // five-second tick. The DISPLAY never reads this: a device that has
  // heard nothing has nothing to show, whatever the anchor says.
  private readingAnchorAt = 0;

  constructor(token: string, handlers: Handlers, deps?: Partial<SocketDeps>) {
    this.token = token;
    this.handlers = handlers;
    this.deps = { ...defaultDeps(), ...(deps ?? {}) };
  }

  start(): void {
    this.closed = false;
    this.readingAnchorAt = this.deps.now();
    // ⚠ CYCLE 433 — THE WATCHDOG RUNS FOR THE WHOLE LIFE OF THE SOCKET,
    // not just while one is open. It used to start in `onopen` and stop in
    // `onclose`, which left `CONNECTING`, the backoff and a blocked
    // consumer entirely unwatched.
    this.startWatchdog();
    this.connect();
  }

  /** Cycle 433 — the consumer's REST reseed answered: the server has told
   *  this client what the newest reading is, which is the same proof a
   *  reading-bearing frame carries. Called by `useGlucoseSocket` when
   *  `/api/glucose/latest` returns. */
  /** ⚠ CYCLE 434 — the bound this socket judges reading age by. Starts at
   *  the fallback constant and is replaced by `useGlucoseSocket` once the
   *  active member's feed has produced enough gaps to derive a cadence.
   *  Clamped here as well as in `readingBoundMs()` so no caller — a test, a
   *  future consumer, a mistake — can hand the transport a bound outside
   *  the floor and the ceiling. Below the floor a healthy feed would blank
   *  its own number; above the ceiling the client would present a reading
   *  the SERVER already considers stale. */
  private readingBound = READING_CONFIRM_BOUND_MS;

  setReadingBound(ms: number): void {
    if (!Number.isFinite(ms)) return;
    const clamped = Math.min(
      READING_BOUND_CEILING_MS,
      Math.max(READING_BOUND_FLOOR_MS, ms),
    );
    this.readingBound = clamped;
    stats.readingBoundMs = clamped;
  }

  /** The bound currently in force. */
  readingBoundMs(): number {
    return this.readingBound;
  }

  noteReadingConfirmed(atMs?: number): void {
    const at = atMs ?? this.deps.now();
    this.readingConfirmedAt = at;
    this.readingAnchorAt = at;
  }

  /** Cycle 435 — the sample time of the reading the CONSUMER is holding
   *  for the active member, so a reopen can report how old the number on
   *  screen was at that moment (`held_reading_age_s`). Telemetry only:
   *  nothing in the transport decides on it. */
  private heldReadingTsMs: number | null = null;

  noteHeldReading(tsMs: number | null): void {
    this.heldReadingTsMs = Number.isFinite(tsMs as number) ? tsMs : null;
  }

  /** What this transport knows at this instant, for the reopen ledger. */
  private snapshot(): ReopenSnapshot {
    const now = this.deps.now();
    return {
      readyState: readyStateLabel(this.ws?.readyState),
      msSinceLastFrame: this.lastFrameAt > 0 ? Math.max(0, Math.round(now - this.lastFrameAt)) : null,
      heldReadingAgeS:
        this.heldReadingTsMs != null
          ? Math.max(0, Math.round((now - this.heldReadingTsMs) / 1000))
          : null,
    };
  }

  /** Epoch ms of the last proof that the reading stream is being heard, or
   *  `null` if there has never been one. */
  readingConfirmedAtMs(): number | null {
    return this.readingConfirmedAt;
  }

  /** The transport state the watchdog is currently judging. */
  transportPhase(): TransportPhase {
    return this.phase;
  }

  private enterPhase(phase: TransportPhase): void {
    this.phase = phase;
    this.phaseSince = this.deps.now();
  }

  /** Underlying readyState, or `CLOSED` when no socket is up. Kept for
   *  callers that want to LOOK; nothing decides on it any more. */
  readyState(): number {
    return this.ws ? this.ws.readyState : WS_READY_CLOSED;
  }

  /** Epoch ms of the last frame of any kind (pongs included). */
  lastFrameAtMs(): number {
    return this.lastFrameAt;
  }

  stop(): void {
    this.closed = true;
    this.teardown();
    // Cycle 433: the watchdog outlives `teardown()` (a reopen must stay
    // watched) and is cleared HERE, once, when the socket is done.
    this.clearWatchdog();
    this.enterPhase("none");
    this.handlers.onStatus("closed");
  }

  /** Cycle 429 — THE WAKE RULE. Close whatever is there (OPEN or not; a
   *  socket that survived a freeze is exactly the one whose readyState
   *  lies) and connect fresh. Frames the old socket delivers after this are
   *  dropped by generation. The new socket's snapshot is the seed; its
   *  first batch is marked catch-up.
   *
   *  ⚠ Cycle 433 narrowed one thing: the backoff ladder is reset for a
   *  `wake` or `manual` reopen only — see below. */
  reopen(reason: ReopenReason): void {
    if (this.closed) return;
    // Cycle 435: the snapshot is taken BEFORE teardown — that is the whole
    // point; after it the socket is gone and the frame clock reset.
    recordReopen(reason, this.snapshot());
    this.teardown();
    this.closed = false;
    // ⚠ Cycle 433 — ONLY A SIGNAL RESETS THE LADDER, NEVER THE WATCHDOG. A
    // wake or a manual reopen is new information (the OS says the app is
    // back), so the backoff starts over. A reopen the WATCHDOG forced is
    // not: resetting there meant a server whose handshake hangs was retried
    // at a flat rate forever with no ladder at all, and it made the "a
    // hidden page resumes the full 30 s cap" rule untrue after any reopen.
    if (reason === "wake" || reason === "manual") {
      this.backoff = BACKOFF_INITIAL_MS;
    }
    // ⚠ Cycle 433 — EVERY reopen re-anchors the reading clock. Two reasons:
    // a link that simply stays quiet is then reopened once per BOUND rather
    // than once per five-second tick; and a socket coming back from a long
    // outage gets a full bound to deliver its snapshot instead of being
    // judged overdue on the very next tick and torn down again at the worst
    // possible moment.
    this.readingAnchorAt = this.deps.now();
    // Cycle 433: a reopen can be triggered before `start()` ever ran (the
    // consumer reopens on wake); make sure the watchdog is up either way.
    if (this.watchdogTimer == null) this.startWatchdog();
    this.connect();
    // ⚠ Cycle 433 — THE READING-AGE CASE RECONNECTS *AND* ASKS. A fresh
    // socket that is as quiet as the old one proves nothing; the REST
    // reseed is what can actually confirm (or refresh) the newest reading.
    // Fired after `connect()` so the socket is already on its way.
    if (reason === "reading_age") {
      try {
        this.handlers.onReseedNeeded?.(reason);
      } catch {
        /* a consumer's reseed must never break the transport */
      }
    }
  }

  private teardown(): void {
    this.gen += 1; // orphan every handler bound to the old socket
    this.clearTimers();
    this.pending = [];
    this.pongDeadline = null;
    if (this.ws) {
      const old = this.ws;
      this.ws = null;
      try {
        old.close();
      } catch {
        /* ignore */
      }
    }
  }

  private connect(): void {
    this.handlers.onStatus(
      this.backoff === BACKOFF_INITIAL_MS ? "connecting" : "reconnecting",
    );
    const gen = this.gen;
    const url = this.deps.urlFor(this.token);
    let ws: SocketLike;
    try {
      ws = this.deps.makeSocket(url);
    } catch (e) {
      console.warn("ws construct failed", e);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.firstBatchAfterConnect = true;
    // Cycle 433: a socket exists and has not opened — the state that had no
    // bound at all before this cycle.
    this.enterPhase("connecting");

    ws.onopen = () => {
      if (gen !== this.gen) return;
      this.lastFrameAt = this.deps.now();
      this.pongDeadline = null;
      this.enterPhase("open");
      this.handlers.onStatus("open");
      this.startPingLoop();
    };
    ws.onmessage = (ev) => {
      if (gen !== this.gen) return; // a frame from a socket we abandoned
      let frame: WSFrame;
      try {
        frame = JSON.parse(String(ev.data)) as WSFrame;
      } catch (e) {
        console.warn("ws frame parse failed", e);
        return;
      }
      // Any frame proves the link works: reset the backoff, feed the
      // watchdog, and settle the outstanding ping.
      this.backoff = BACKOFF_INITIAL_MS;
      const now = this.deps.now();
      // A thaw can be noticed HERE as well as on the tick: if this frame
      // lands long after the watchdog last ran, the timers were frozen and
      // this is the start of a backlog — whichever of the two the event
      // loop happens to dispatch first.
      if (this.lastTickAt != null && now - this.lastTickAt > THAW_GAP_MS) {
        this.thawedUntil = now + THAW_CATCHUP_WINDOW_MS;
      }
      this.lastFrameAt = now;
      if ((frame as { type?: string }).type === "pong") {
        this.pongDeadline = null;
        return;
      }
      // ⚠ CYCLE 433 — A READING-BEARING FRAME IS THE PROOF OF CURRENCY.
      // `snapshot` (every member's latest) and `glucose` are the server
      // saying what the newest reading is; every other frame — acks,
      // heartbeats, reminders, and the pong that returned above — proves
      // only that a link exists. The 2026-09-13 socket had a link.
      const kind = (frame as { type?: string }).type;
      if (kind === "snapshot" || kind === "glucose") {
        this.readingConfirmedAt = now;
        this.readingAnchorAt = now;
      }
      this.pending.push(frame);
      if (this.flushTimer == null) {
        this.flushTimer = this.deps.setTimeout(() => {
          this.flushTimer = null;
          this.flush();
        }, BATCH_WINDOW_MS);
      }
    };
    ws.onerror = () => {
      // onclose handles reconnect; just log here.
    };
    ws.onclose = () => {
      if (gen !== this.gen) return;
      this.clearPingLoop();
      // ⚠ CYCLE 433 — THE WATCHDOG IS *NOT* CLEARED HERE ANY MORE. Clearing
      // it on close is what left the backoff, and the connect that follows
      // it, unwatched: the dead socket of 2026-09-13 23:14:51 was not
      // noticed until 23:23:08.
      this.ws = null;
      if (this.closed) return;
      this.scheduleReconnect();
    };
  }

  private flush(): void {
    if (this.pending.length === 0) return;
    const frames = this.pending;
    this.pending = [];
    const now = this.deps.now();
    const catchUp = classifyBatch(
      frames.length,
      now,
      this.thawedUntil,
      this.firstBatchAfterConnect,
    );
    this.firstBatchAfterConnect = false;
    this.handlers.onFrames(frames, {
      receivedAtMs: now,
      catchUp,
      size: frames.length,
    });
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.handlers.onStatus("reconnecting");
    // ⚠ CYCLE 433 — THE BACKOFF IS CAPPED WHILE THE PAGE IS VISIBLE. The
    // 1 s → 30 s ladder is right for a backgrounded tab and wrong for the
    // screen someone is looking at during a low. The ladder itself is
    // unchanged (`this.backoff` still doubles, so a page that goes hidden
    // resumes the full ladder); only the DELAY actually waited is capped.
    const cap = this.deps.isVisible() ? VISIBLE_BACKOFF_MAX_MS : BACKOFF_MAX_MS;
    const delay = Math.min(this.backoff, cap);
    this.enterPhase("waiting");
    this.reconnectTimer = this.deps.setTimeout(() => {
      this.reconnectTimer = null;
      this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
      this.connect();
    }, delay);
  }

  private startPingLoop(): void {
    this.clearPingLoop();
    this.pingTimer = this.deps.setInterval(() => {
      if (this.ws && this.ws.readyState === WS_READY_OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: "ping" }));
          // Cycle 429: the pong is WATCHED. One deadline at a time — a
          // ping sent while the last one is unanswered keeps the earlier,
          // stricter deadline.
          if (this.pongDeadline == null) {
            this.pongDeadline = this.deps.now() + PONG_TIMEOUT_MS;
          }
        } catch {
          /* ignore */
        }
      }
    }, PING_INTERVAL_MS);
  }

  private clearPingLoop(): void {
    if (this.pingTimer != null) {
      this.deps.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /** Cycle 429 — the frame-age watchdog; cycle 433 — every state's
   *  watchdog. Every tick: detect a thaw (a tick that arrives late means
   *  the timers were frozen, so mark the next moments as catch-up), then
   *  judge a VISIBLE page's transport by the bound that belongs to the
   *  state it is actually in. Started once, in `start()`, and cleared once,
   *  in `stop()`. */
  private startWatchdog(): void {
    this.clearWatchdog();
    this.lastTickAt = this.deps.now();
    this.watchdogTimer = this.deps.setInterval(() => this.tick(), WATCHDOG_TICK_MS);
  }

  /** Exposed for tests; production reaches it through the interval. */
  tick(): void {
    const now = this.deps.now();
    if (this.lastTickAt != null && now - this.lastTickAt > THAW_GAP_MS) {
      this.thawedUntil = now + THAW_CATCHUP_WINDOW_MS;
    }
    this.lastTickAt = now;
    if (this.closed) return;
    const visible = this.deps.isVisible();
    // ⚠ CYCLE 433 — ONE TICK, EVERY STATE. `this.ws == null` used to end
    // the tick here, which meant the backoff and everything after a close
    // went unjudged. The phase says which bound applies.
    if (this.phase === "open" && this.ws != null) {
      const verdict =
        watchdogVerdict({
          nowMs: now,
          visible,
          lastFrameAtMs: this.lastFrameAt,
          pongDeadlineMs: this.pongDeadline,
        }) ??
        readingAgeVerdict({
          nowMs: now,
          visible,
          readingConfirmedAtMs: this.readingAnchorAt,
          boundMs: this.readingBound,
        });
      if (verdict != null) this.reopen(verdict);
      return;
    }
    const pending = pendingVerdict({
      nowMs: now,
      visible,
      phase: this.phase,
      phaseSinceMs: this.phaseSince,
    });
    if (pending != null) this.reopen(pending);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer != null) {
      this.deps.clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.lastTickAt = null;
  }

  /** ⚠ CYCLE 433 — the watchdog is deliberately NOT in here. `teardown()`
   *  runs on every reopen, and a reopen is exactly the moment the watchdog
   *  must keep running: the socket it is about to build may never open. */
  private clearTimers(): void {
    this.clearPingLoop();
    if (this.reconnectTimer != null) {
      this.deps.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.flushTimer != null) {
      this.deps.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}
